import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Select from 'react-select';
import PropTypes from 'prop-types';
import { toast } from "react-toastify";
import { FaInfoCircle, FaUserFriends } from "react-icons/fa";
import apiAxios from "helpers/apiAxios";
import { assignUserTravelBookingSubmitterRecord } from "redux/authorization/userSlice";
import { selectIconComponents, reactSelectCompactStyles } from 'helpers/reactSelectIconControl';

// Stable reference — see note in AssignRole; inline components break the menu.
const SUBMITTER_SELECT_COMPONENTS = selectIconComponents(<FaUserFriends size={14} className="tw-text-cyan-500" />);
// Active users fit in one page; public_list caps per_page server-side otherwise.
const PUBLIC_USERS_PER_PAGE = 1000;

const toOption = (user) => ({
  value: user.id,
  label: user.full_name ? `${user.name} — ${user.full_name}` : user.name,
});

// Record-level "Travel booking submitters": the user (typically an assistant)
// gets read-only access to every travel booking submitted by the chosen users,
// e.g. to help collect claim documents.
const RecordLevelTravelBookingSubmitter = ({ userId, data = [] }) => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected((data || []).map(toOption));
  }, [userId, data]);

  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    apiAxios.get('/authorization/users/public_list', { params: { per_page: PUBLIC_USERS_PER_PAGE } })
      .then((response) => { if (!cancelled) setUsers(response.data?.rows || []); })
      .catch(() => { if (!cancelled) toast.error('Failed to load users'); })
      .finally(() => { if (!cancelled) setLoadingUsers(false); });
    return () => { cancelled = true; };
  }, []);

  // The user always sees their own bookings, so they are not offered as a submitter.
  const options = useMemo(
    () => users.filter(user => user.id !== userId).map(toOption),
    [users, userId]
  );

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!userId) return;

    setSaving(true);
    try {
      await dispatch(assignUserTravelBookingSubmitterRecord({
        userId,
        data: { submitter_ids: selected.map(option => option.value) },
      })).unwrap();
    } catch (error) {
      toast.error(error?.message || 'Failed to assign travel booking submitters');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="tw-mb-2 tw-text-xs">
          <Select
            components={SUBMITTER_SELECT_COMPONENTS}
            styles={reactSelectCompactStyles}
            closeMenuOnSelect={false}
            options={options}
            value={selected}
            isLoading={loadingUsers}
            placeholder='Select users...'
            classNamePrefix='react-select'
            isMulti
            onChange={(options) => setSelected(options || [])}
          />
        </div>

        <div className="tw-flex tw-justify-end tw-mt-2">
          <button
            type="submit"
            disabled={saving}
            className="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-border tw-border-gray-300 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-700 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
          >
            {data.length > 0 ? "Update" : "Add"} Submitters
          </button>
        </div>
      </form>

      <div className="tw-mt-4 tw-text-xs">
        <FaInfoCircle /> View-only access to travel bookings submitted by these users (e.g. an assistant helping with claim documents).
      </div>
    </>
  );
};

RecordLevelTravelBookingSubmitter.propTypes = {
  userId: PropTypes.number,
  data: PropTypes.array,
};

export default RecordLevelTravelBookingSubmitter;
